import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PortfolioKeys } from "web_myplann/Api/Portfolio/portfolioKeys";
import { withPerformedAction } from "web_myplann/Api/Portfolio/withPerformedAction";
import {
	type AlertBoxActionEnum,
	type AlertBoxActionStateEnum,
	type PortfolioAlertBoxListResponseDTO,
	type PortfolioEventTypeEnum,
	postPortfolioIdAlertBoxesActionV2,
} from "web_myplann/Infrastructure/Api/Api";
import { processResponse } from "web_myplann/Infrastructure/Api/ApiUtils";

type RecordActionVariables = {
	contractId: number;
	eventType: PortfolioEventTypeEnum;
	eventDate: string;
	action: AlertBoxActionEnum;
	state: AlertBoxActionStateEnum;
};

/**
 * Records that a CTA action was performed on an Event Alert (POST
 * /v2/portfolio/{id}/alert-boxes/action). Used for actions whose "performed"
 * state the backend cannot derive on its own — currently the insurance buttons'
 * fallback "Opened" state. On success it eagerly patches the cached alert so the
 * subtext appears immediately; the backend reconciles `performedAt` on the next
 * refetch.
 */
export const useRecordAlertBoxActionMutation = () => {
	const queryClient = useQueryClient();

	return useMutation<void, Error, RecordActionVariables>({
		mutationFn: processResponse(
			({
				contractId,
				eventType,
				eventDate,
				action,
				state,
			}: RecordActionVariables) =>
				postPortfolioIdAlertBoxesActionV2(
					{ eventType, eventDate, action, state },
					contractId,
				),
		),
		onSuccess: (_data, { contractId, eventType, eventDate, action, state }) => {
			queryClient.setQueriesData<PortfolioAlertBoxListResponseDTO | undefined>(
				{ queryKey: PortfolioKeys.alertBoxesAll(contractId) },
				(previous) => {
					if (!previous) {
						return previous;
					}
					return {
						...previous,
						alerts: previous.alerts.map((alert) =>
							alert.eventType === eventType && alert.eventDate === eventDate
								? withPerformedAction(alert, action, state)
								: alert,
						),
					};
				},
			);
		},
	});
};
